import { useState } from "react";
import { toast } from "sonner";
import { Loader2, MapPin, Phone, Mail, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useSite, usePageSeo } from "@/context/SiteContext";

const inputCls = "bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400 focus-visible:ring-primary rounded-sm h-11";

export default function ContactPage() {
  const { content } = useSite();
  const page = content.page_contact || {};
  usePageSeo(page.seo_title || "Contact", page.seo_description || page.hero_description);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/leads", { lead_type: "general_inquiry", ...form });
      toast.success("Message sent! We'll get back to you shortly.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const hasInfo = page.address || page.phone || page.email;

  return (
    <div className="min-h-screen bg-background" data-testid="contact-page">
      <div className="bg-zinc-950 border-b border-zinc-800 py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black uppercase tracking-tight text-white">
            {page.hero_title || "Contact Us"}
          </h1>
          {page.hero_description && (
            <p className="text-zinc-400 mt-3 max-w-2xl">{page.hero_description}</p>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div className="space-y-5">
            {hasInfo && (
              <div className="bg-card border border-[#262626] p-6">
                <h2 className="font-display text-xl font-bold uppercase text-foreground mb-5">Showroom</h2>
                <ul className="space-y-4 text-sm text-zinc-600">
                  {page.address && (
                    <li className="flex items-start gap-3"><MapPin className="h-5 w-5 text-primary shrink-0" /> {page.address}</li>
                  )}
                  {page.phone && (
                    <li className="flex items-center gap-3"><Phone className="h-5 w-5 text-primary shrink-0" /> <a href={`tel:${page.phone}`} className="hover:text-primary">{page.phone}</a></li>
                  )}
                  {page.email && (
                    <li className="flex items-center gap-3"><Mail className="h-5 w-5 text-primary shrink-0" /> <a href={`mailto:${page.email}`} className="hover:text-primary">{page.email}</a></li>
                  )}
                </ul>
              </div>
            )}

            {page.hours?.length > 0 && (
              <div className="bg-card border border-[#262626] p-6">
                <h2 className="font-display text-xl font-bold uppercase text-foreground mb-5 flex items-center gap-2">
                  <Clock className="h-5 w-5 text-primary" /> Hours
                </h2>
                <div className="space-y-2.5" data-testid="contact-hours">
                  {page.hours.map((h, i) => (
                    <div key={i} className="flex justify-between text-sm border-b border-zinc-200 pb-2.5">
                      <span className="text-zinc-500">{h.day}</span>
                      <span className="text-zinc-800 font-medium">{h.time}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {page.departments?.length > 0 && (
              <div className="bg-card border border-[#262626] p-6">
                <h2 className="font-display text-xl font-bold uppercase text-foreground mb-5">Direct Lines</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {page.departments.map((d, i) => (
                    <div key={i} className="bg-zinc-50 border border-zinc-200 p-4" data-testid={`contact-dept-${i}`}>
                      <p className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">{d.name}</p>
                      <p className="text-primary font-bold">{d.line}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {page.form_enabled !== false && (
            <div className="bg-card border border-[#262626] p-6 sm:p-8 h-fit">
              <h2 className="font-display text-xl font-bold uppercase text-foreground mb-2">Send Us A Message</h2>
              <p className="text-sm text-zinc-500 mb-6">We typically respond within one business day.</p>
              <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="contact-name" className="text-xs font-mono uppercase tracking-wider text-zinc-500">Full Name *</Label>
                    <Input id="contact-name" required value={form.name} onChange={set("name")} className={inputCls} data-testid="contact-name-input" />
                  </div>
                  <div>
                    <Label htmlFor="contact-phone" className="text-xs font-mono uppercase tracking-wider text-zinc-500">Phone *</Label>
                    <Input id="contact-phone" required value={form.phone} onChange={set("phone")} className={inputCls} data-testid="contact-phone-input" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="contact-email" className="text-xs font-mono uppercase tracking-wider text-zinc-500">Email *</Label>
                  <Input id="contact-email" required type="email" value={form.email} onChange={set("email")} className={inputCls} data-testid="contact-email-input" />
                </div>
                <div>
                  <Label htmlFor="contact-message" className="text-xs font-mono uppercase tracking-wider text-zinc-500">Message *</Label>
                  <Textarea id="contact-message" required value={form.message} onChange={set("message")} className={`${inputCls} min-h-[140px]`} data-testid="contact-message-input" />
                </div>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full h-12 bg-primary hover:bg-primary-hover text-primary-foreground font-bold uppercase tracking-wider rounded-sm"
                  data-testid="contact-submit-button"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send Message"}
                </Button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
